import { useEffect, useCallback, useState, useRef } from 'react'
import {
  User, Coins, Clock, Activity, CreditCard, Cpu,
  History, BarChart3, Camera, Key, Shield, AlertTriangle, Trash2,
  ChevronDown, ChevronUp, Plus, Minus, Check, X, Loader2, Edit3, Upload,
  FileText, ArrowLeftRight
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import client from '../api/client'
import { Transaction } from '../types'
import toast from 'react-hot-toast'
import { getCached, setCache } from '../utils/cache'
// TranslatorHistory removed — replaced by inline transaction history
// import { tokensToStars, formatBalance } from '../utils/stars'

interface ModelSettings {
  chat_models: string[] | null
  image_models: string[] | null
  video_models: string[] | null
  music_models: string[] | null
}

interface AvailableModels {
  chat: string[]
  image: string[]
  video: string[]
  music: string[]
}

function ModelPricesTable({ prices }: { prices: Record<string, any> }) {
  const cats: Record<string, string> = { chat: '💬 Чат', image: '🎨 Изображения', video: '🎬 Видео', music: '🎵 Музыка', edit: '✏️ Редактор' }
  const grouped: Record<string, [string, any][]> = {}
  for (const [key, val] of Object.entries(prices)) {
    const cat = val.category || 'chat'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push([key, val])
  }
  const catKeys = Object.keys(grouped).sort((a, b) => {
    const order = ['chat', 'image', 'video', 'music', 'edit']
    return order.indexOf(a) - order.indexOf(b)
  })
  const [activeCat, setActiveCat] = useState(catKeys[0] || 'chat')

  return (
    <>
      {/* Вкладки */}
      {catKeys.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {catKeys.map((ck) => (
            <button key={ck} onClick={() => setActiveCat(ck)} className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${activeCat === ck ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'text-gray-500 hover:text-gray-300 bg-white/[0.03] border border-transparent'}`}>
              {cats[ck] || ck}
            </button>
          ))}
        </div>
      )}
      {/* Список моделей активной вкладки */}
      <div className="max-h-72 overflow-y-auto space-y-0.5 pr-1">
        {(grouped[activeCat] || []).map(([modelKey, priceData]) => (
          <div key={modelKey} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/[0.03]">
            <span className="text-[11px] text-gray-400 truncate mr-2 flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${priceData.unit === 'stars' ? 'bg-amber-400' : 'bg-indigo-400'}`} />
              {priceData.display_name || modelKey}
            </span>
            <span className="text-[11px] text-gray-300 font-medium shrink-0">
              {priceData.unit === 'stars_sec'
                ? `✦ ${priceData.input || 1} / сек`
                : priceData.unit === 'stars'
                  ? `✦ ${priceData.input || 1} за генерацию`
                  : priceData.input + '/' + priceData.output + ' CN'}
              {priceData.unit === 'tokens' && <span className="text-[9px] text-indigo-400/70 ml-1">(input/output)</span>}
            </span>
          </div>
        ))}
      </div>
    </>
  )
}

interface SpendStats {
  chat: { total: number; models: Record<string, number> }
  image: { total: number; models: Record<string, number> }
  video: { total: number; models: Record<string, number> }
  music: { total: number; models: Record<string, number> }
}

function StatCard({ label, total, color }: { label: string; total: number; color: string }) {
  return (
    <div className="bg-white/[0.03] rounded-xl p-3">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-semibold ${color}`}>{total.toLocaleString()}</p>
    </div>
  )
}

function SpendBarChart({ stats }: { stats: SpendStats }) {
  const cats = [
    { key: 'chat', label: 'Чат', color: 'bg-indigo-500' },
    { key: 'image', label: 'Изобр.', color: 'bg-green-500' },
    { key: 'video', label: 'Видео', color: 'bg-purple-500' },
    { key: 'music', label: 'Музыка', color: 'bg-pink-500' },
  ]
  const maxTotal = Math.max(...cats.map((c) => stats[c.key as keyof SpendStats]?.total || 0), 1)
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 mb-1">График использования (токены)</p>
      {cats.map((cat) => {
        const total = stats[cat.key as keyof SpendStats]?.total || 0
        const pct = Math.round((total / maxTotal) * 100)
        return (
          <div key={cat.key} className="flex items-center gap-3">
            <span className="text-[10px] text-gray-500 w-12 shrink-0">{cat.label}</span>
            <div className="flex-1 h-4 bg-black/30 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${cat.color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-gray-400 w-16 text-right">{total.toLocaleString()}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function Profile() {
  const { user, logout } = useAuthStore()
  const { balance, starsBalance, totalUsed, fetchBalance } = useTokenStore()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [lastLogin, setLastLogin] = useState<string | null>(null)

  const [showStats, setShowStats] = useState(false)
  const [showEditProfile, setShowEditProfile] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showDangerZone, setShowDangerZone] = useState(false)

  const [editName, setEditName] = useState('')
  const [editBio, setEditBio] = useState('')
  const [editAvatarUrl, setEditAvatarUrl] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [changingPw, setChangingPw] = useState(false)

  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)

  const [spendStats, setSpendStats] = useState<SpendStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  const [modelSettings, setModelSettings] = useState<ModelSettings | null>(null)
  const [availableModels, setAvailableModels] = useState<AvailableModels | null>(null)
  const [activeModelsTab, setActiveModelsTab] = useState<'chat' | 'image' | 'video' | 'music' | null>(null)
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [modelPrices, setModelPrices] = useState<Record<string, any> | null>(() => getCached('profile_model_prices'))
  const [exchangeRate, setExchangeRate] = useState<number | null>(null)

  useEffect(() => {
    fetchBalance()
    loadTransactions()
    loadLastLogin()
    loadModels()
    loadProfile()
    loadModelPrices()
    loadExchangeRate()
  }, [fetchBalance])

  const loadModelPrices = async () => {
    try {
      const { data } = await client.get('/admin/ai-models/calculated-prices')
      setCache('profile_model_prices', data)
      setModelPrices(data)
    } catch {}
  }

  const loadExchangeRate = async () => {
    try {
      const { data } = await client.get('/admin/ai-models/usd-rate')
      setExchangeRate(data.rate)
    } catch {}
  }

  const loadTransactions = async () => {
    try {
      const { data } = await client.get('/tokens/transactions')
      const all = data.transactions ?? []
      all.sort((a: Transaction, b: Transaction) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      setTransactions(all)
    } catch {
      setTransactions([])
    }
  }

  const loadLastLogin = async () => {
    try {
      const { data } = await client.get('/user/last-login')
      setLastLogin(data.last_login)
    } catch {}
  }

  const loadProfile = async () => {
    try {
      const { data } = await client.get('/users/profile')
      setEditName(data.name || '')
      setEditBio(data.bio || '')
      setEditAvatarUrl(data.avatar_url || '')
      // Обновить user в глобальном store (чтобы план и другие поля не застаивались)
      useAuthStore.setState((s: any) => ({ user: { ...s.user, plan: data.plan, name: data.name, avatar_url: data.avatar_url } }))
    } catch {}
  }

  const loadModels = async () => {
    try {
      const [my, avail] = await Promise.all([client.get('/models/my'), client.get('/models/available')])
      setModelSettings(my.data)
      setAvailableModels(avail.data)
    } catch {}
  }

  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const { data } = await client.get('/tokens/stats')
      setSpendStats(data)
    } catch {
      toast.error('Не удалось загрузить статистику')
    } finally {
      setLoadingStats(false)
    }
  }

  const handleSaveProfile = async () => {
    setSavingProfile(true)
    try {
      await client.put('/users/profile', { name: editName, bio: editBio, avatar_url: editAvatarUrl })
      toast.success('Профиль сохранён')
      setShowEditProfile(false)
    } catch {
      toast.error('Ошибка при сохранении')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleUploadAvatar = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    setUploadingAvatar(true)
    try {
      const { data } = await client.post('/upload/image', formData)
      setEditAvatarUrl(data.url)
      toast.success('Фото загружено')
    } catch {
      toast.error('Ошибка загрузки фото')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Пароли не совпадают'); return }
    if (newPw.length < 6) { toast.error('Новый пароль должен быть не менее 6 символов'); return }
    setChangingPw(true)
    try {
      await client.post('/users/change-password', { current_password: currentPw, new_password: newPw })
      toast.success('Пароль изменён')
      setShowChangePassword(false)
      setCurrentPw(''); setNewPw(''); setConfirmPw('')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка при смене пароля')
    } finally {
      setChangingPw(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      await client.delete('/users/account')
      toast.success('Аккаунт удалён')
      logout()
    } catch {
      toast.error('Ошибка при удалении')
    } finally {
      setDeleting(false)
    }
  }

  const modelLabels: Record<string, string> = { chat: 'Чат', image: 'Изображения', video: 'Видео', music: 'Музыка' }

  const openModelSettings = (cat: 'chat' | 'image' | 'video' | 'music') => {
    if (!availableModels?.[cat]) return
    const enabled = modelSettings?.[`${cat}_models` as keyof ModelSettings] as string[] | null
    setSelectedModels(new Set(enabled && enabled.length > 0 ? enabled : []))
    setActiveModelsTab(cat)
  }

  const toggleModel = (model: string) => {
    setSelectedModels((prev) => {
      const next = new Set(prev)
      if (next.has(model)) next.delete(model); else next.add(model)
      return next
    })
  }

  const selectAllModels = () => {
    if (!activeModelsTab || !availableModels?.[activeModelsTab]) return
    const all = availableModels[activeModelsTab]
    setSelectedModels(selectedModels.size === all.length ? new Set() : new Set(all))
  }

  const saveModels = async () => {
    if (!activeModelsTab) return
    setSaving(true)
    try {
      const key = `${activeModelsTab}_models` as keyof ModelSettings
      const updated = { ...modelSettings, [key]: Array.from(selectedModels) }
      await client.put('/models/my', updated)
      setModelSettings(updated as ModelSettings)
      setActiveModelsTab(null)
      toast.success('Модели сохранены')
    } catch {
      toast.error('Ошибка при сохранении')
    } finally {
      setSaving(false)
    }
  }

  const enabledCount = (cat: string) => {
    const list = modelSettings?.[`${cat}_models` as keyof ModelSettings] as string[] | null
    if (!list || list.length === 0) return 0
    return list.filter((m) => availableModels?.[cat as keyof AvailableModels]?.includes(m)).length
  }

  const totalCount = (cat: string) => availableModels?.[cat as keyof AvailableModels]?.length ?? 0

  const getPlanName = () => {
    if (!user?.plan || user.plan === 'free') return 'Бесплатный'
    const names: Record<string, string> = {
      starter: 'Начальный',
      standard: 'Стандарт',
      promax: 'Комфорт',
      premium: 'Премиум',
      max: 'Макс',
    }
    return names[user.plan] || user.plan
  }

  const getPlanColor = () => {
    if (!user?.plan || user.plan === 'free') return 'text-gray-400'
    return 'text-indigo-400'
  }

  if (!user) {
    return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-gray-500 text-sm">Загрузка...</div>
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* ===== Profile header ===== */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            {editAvatarUrl ? (
              <img src={editAvatarUrl} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-indigo-500/30" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                {user.email?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white truncate">{user.name || user.email}</h2>
              <button onClick={() => { setShowEditProfile(true); loadProfile() }} className="p-1 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all" title="Редактировать профиль">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-gray-500">{user.email}</p>
            {user.bio && <p className="text-xs text-gray-400 mt-1 italic">{user.bio}</p>}
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-medium ${getPlanColor()}`}>{getPlanName()}</span>
              {user.role === 'admin' && <span className="text-[10px] text-gray-600">• Администратор</span>}
            </div>
            {lastLogin && <p className="text-[10px] text-gray-600 mt-0.5">Последний вход: {new Date(lastLogin).toLocaleString('ru-RU').replace(',', '')}</p>}
          </div>
        </div>
      </div>

      {/* ===== Balance & usage ===== */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-gray-500">Баланс</span>
          </div>
          <p className="text-2xl font-bold text-yellow-300">{balance.toLocaleString()}</p>
          <p className="text-xs text-yellow-500/60">✦{starsBalance.toLocaleString()} звёзд</p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-gray-500">Использовано</span>
          </div>
          <p className="text-2xl font-bold text-blue-300">{totalUsed.toLocaleString()}</p>
        </div>
      </div>

      {/* ===== Quick actions ===== */}
      <div className="grid grid-cols-3 gap-3">
        <a href="/payment" className="glass-card p-4 text-center hover:bg-white/[0.04] transition-all cursor-pointer">
          <CreditCard className="w-5 h-5 text-green-400 mx-auto mb-1" />
          <span className="text-xs text-gray-400">Пополнить</span>
        </a>
        <a href="/pricing" className="glass-card p-4 text-center hover:bg-white/[0.04] transition-all cursor-pointer">
          <History className="w-5 h-5 text-purple-400 mx-auto mb-1" />
          <span className="text-xs text-gray-400">Тарифы</span>
        </a>
        <button onClick={() => { setShowStats(!showStats); if (!spendStats) loadStats() }} className="glass-card p-4 text-center hover:bg-white/[0.04] transition-all cursor-pointer">
          <BarChart3 className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
          <span className="text-xs text-gray-400">Статистика</span>
        </button>
      </div>

      {/* ===== Статистика ===== */}
      {showStats && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Статистика расходов</h3>
            {loadingStats && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
          </div>
          {loadingStats ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
          ) : spendStats ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Чат" total={spendStats.chat.total} color="text-indigo-400" />
                <StatCard label="Изображения" total={spendStats.image.total} color="text-green-400" />
                <StatCard label="Видео" total={spendStats.video.total} color="text-purple-400" />
                <StatCard label="Музыка" total={spendStats.music.total} color="text-pink-400" />
              </div>
              <SpendBarChart stats={spendStats} />
              {Object.entries(spendStats).map(([category, catData]) => {
                const modelEntries = Object.entries(catData.models).filter(([, v]) => v > 0)
                if (modelEntries.length === 0) return null
                return (
                  <div key={category}>
                    <h4 className="text-xs font-medium text-gray-400 mb-2 capitalize">{modelLabels[category as keyof typeof modelLabels] || category}</h4>
                    <div className="space-y-1">
                      {modelEntries// eslint-disable-next-line
                .sort((a: any, b: any) => b[1] - a[1]).map(([model, spent]: [string, number]) => (
                        <div key={model} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                          <span className="text-xs text-gray-400 truncate mr-2">{model}</span>
                          <span className="text-xs text-gray-200 font-medium">{spent.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-600 text-center py-6">Нет данных для отображения</p>
          )}
        </div>
      )}

      {/* ===== История транзакций ===== */}
      {/* Старая история операций под кнопкой "История" — удалена, заменена на постоянный вид выше */}

      {/* ===== Модели ===== */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2"><Cpu className="w-4 h-4" />Мои модели</h3>
        <p className="text-xs text-gray-500 mb-4">Выберите, какие AI-модели будут доступны вам в чате и генераторах</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(['chat', 'image', 'video', 'music'] as const).map((cat) => (
            <button key={cat} onClick={() => openModelSettings(cat)} className="glass-card p-4 text-left hover:bg-white/[0.04] transition-all cursor-pointer">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${enabledCount(cat) > 0 ? 'bg-green-400' : 'bg-gray-600'}`} />
                <span className="text-sm font-medium text-gray-200">{modelLabels[cat]}</span>
              </div>
              <p className="text-xs text-gray-500">{enabledCount(cat)}/{totalCount(cat)} моделей</p>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Тарифный план ===== */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" />Тарифный план</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-lg font-bold ${getPlanColor()}`}>{getPlanName()}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {!user?.plan || user.plan === 'free' ? 'Бесплатные модели доступны без ограничений' : 'Платные модели доступны по токенам'}
            </p>
          </div>
          <a href="/payment" className="gradient-btn px-4 py-2 rounded-xl text-xs">
            {!user?.plan || user.plan === 'free' ? 'Перейти на платный' : 'Изменить тариф'}
          </a>
        </div>
      </div>

      {/* ===== История операций (всегда видима) ===== */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2"><Clock className="w-4 h-4" />История операций</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{transactions.length} записей</span>
          </div>
        </div>
        {transactions.length === 0 ? (
          <p className="text-xs text-gray-600 text-center py-6">История пуста</p>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-1">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${tx.type == 'reward' || tx.type === 'bonus' || tx.amount > 0 ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="text-xs text-gray-400 truncate">{tx.description || tx.type || 'Операция'}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{tx.amount > 0 ? `+${tx.amount}` : tx.amount}</span>
                  <span className="text-[10px] text-gray-600">{new Date(tx.created_at).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== Цены моделей ===== */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Цены моделей</h3>
        <p className="text-xs text-gray-500 mb-3">Стоимость использования моделей в CN-токенах (✦ — для генерации изображений/видео/музыки)</p>
        <div className="mb-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 space-y-1">
          <p className="text-[11px] text-gray-400">
            1 000 000 CN = <span className="text-gray-200">≈ {exchangeRate ? (exchangeRate).toFixed(2) : '..'} ₽</span>
            <span className="text-gray-600 ml-1">(курс ЦБ РФ)</span>
          </p>
          <p className="text-[11px] text-gray-400">
            1 ✦ = <span className="text-gray-200">≈ {exchangeRate ? (exchangeRate / 100).toFixed(2) : '..'} ₽</span>
            <span className="text-gray-600"> — для изображений, видео и музыки</span>
          </p>
          <p className="text-[10px] text-gray-600 mt-1">
            CN-токены и ✦ начисляются при пополнении баланса. Подробнее — на странице <a href="/pricing" className="text-indigo-400 hover:text-indigo-300">тарифов</a>.
          </p>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {modelPrices ? <ModelPricesTable prices={modelPrices} /> : (
            <p className="text-xs text-gray-600 text-center py-4">Загрузка...</p>
          )}
        </div>
      </div>

      {/* ===== Опасная зона ===== */}
      <div className="glass-card p-6 border border-red-500/20">
        <h3 className="text-sm font-semibold text-red-400 mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Опасная зона</h3>
        <div className="mb-4">
          <button onClick={() => setShowChangePassword(!showChangePassword)} className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-all">
            <Key className="w-3.5 h-3.5" />Сменить пароль{showChangePassword ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showChangePassword && (
            <div className="mt-3 space-y-3 p-3 rounded-xl bg-black/20">
              <input type="password" placeholder="Текущий пароль" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="input-field text-sm w-full" />
              <input type="password" placeholder="Новый пароль (мин. 6 символов)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="input-field text-sm w-full" />
              <input type="password" placeholder="Повторите новый пароль" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="input-field text-sm w-full" />
              <button onClick={handleChangePassword} disabled={changingPw || !currentPw || !newPw || !confirmPw} className="gradient-btn w-full py-2 rounded-xl text-xs flex items-center justify-center gap-2 disabled:opacity-40">
                {changingPw ? 'Сохранение...' : 'Сменить пароль'}
              </button>
            </div>
          )}
        </div>
        <div>
          <button onClick={() => setShowDangerZone(!showDangerZone)} className="flex items-center gap-2 text-xs text-red-400 hover:text-red-300 transition-all">
            <Trash2 className="w-3.5 h-3.5" />Удалить аккаунт{showDangerZone ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showDangerZone && (
            <div className="mt-3 space-y-3 p-3 rounded-xl bg-red-500/5 border border-red-500/20">
              <p className="text-xs text-red-400">Удаление аккаунта приведёт к полной потере всех данных: чатов, изображений, видео, музыки и токенов. Это действие необратимо.</p>
              <input type="text" placeholder="Введите слово 'удалить' для подтверждения" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="input-field text-sm w-full" />
              <button onClick={handleDeleteAccount} disabled={deleting || deleteConfirm !== 'удалить'} className="w-full py-2 rounded-xl text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 disabled:opacity-30 transition-all flex items-center justify-center gap-2">
                {deleting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Удаление...</> : <><Trash2 className="w-3.5 h-3.5" />Полностью удалить аккаунт</>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ===== Модалка выбора моделей ===== */}
      {activeModelsTab && availableModels && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActiveModelsTab(null)}>
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-sm font-semibold text-gray-200">Модели для {modelLabels[activeModelsTab]}</h3>
              <button onClick={() => setActiveModelsTab(null)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-4 py-2 border-b border-white/5">
              <button onClick={selectAllModels} className="text-xs text-indigo-400 hover:text-indigo-300 transition-all">
                {selectedModels.size === availableModels[activeModelsTab].length ? 'Снять все' : 'Выбрать все'}
              </button>
              <span className="text-xs text-gray-600 ml-2">({selectedModels.size}/{availableModels[activeModelsTab].length})</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {availableModels[activeModelsTab].map((model) => (
                <label key={model} onClick={() => toggleModel(model)} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${selectedModels.has(model) ? 'bg-indigo-500/10 text-indigo-200' : 'text-gray-400 hover:bg-white/[0.03] hover:text-gray-200'}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedModels.has(model) ? 'bg-indigo-500 border-indigo-400' : 'border-gray-600'}`}>
                    {selectedModels.has(model) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className="text-sm truncate">{model}</span>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-white/5">
              <button onClick={saveModels} disabled={saving} className="gradient-btn w-full py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Модалка редактирования профиля ===== */}
      {showEditProfile && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowEditProfile(false)}>
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2"><Edit3 className="w-4 h-4" />Редактировать профиль</h3>
              <button onClick={() => setShowEditProfile(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1"><Camera className="w-3 h-3" />Фото профиля</label>
              <div className="flex items-center gap-3">
                <div className="relative">
                  {editAvatarUrl ? (
                    <img src={editAvatarUrl} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-500/30" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
                      {user.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleUploadAvatar(file)
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="gradient-btn px-4 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-40"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {uploadingAvatar ? 'Загрузка...' : 'Загрузить фото'}
                  </button>
                  {editAvatarUrl && (
                    <button
                      onClick={() => setEditAvatarUrl('')}
                      className="ml-2 text-[10px] text-red-400 hover:text-red-300"
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1"><User className="w-3 h-3" />Имя</label>
              <input type="text" placeholder="Ваше имя" value={editName} onChange={(e) => setEditName(e.target.value)} className="input-field w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">О себе</label>
              <textarea placeholder="Напишите пару слов о себе" value={editBio} onChange={(e) => setEditBio(e.target.value)} rows={3} className="input-field w-full resize-none" />
            </div>
            <button onClick={handleSaveProfile} disabled={savingProfile} className="gradient-btn w-full py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40">
              {savingProfile ? 'Сохранение...' : 'Сохранить профиль'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
